import React, { useEffect } from 'react'
import type { CandleInterval } from '@nutrimatic/sdk'

import { useTranslation } from '@/features/locale/LocaleProvider'
import { MarketDataEmpty, MarketDataSkeleton } from '@/data/marketApi/components/MarketDataStates'
import { NutrimaticCandleChart } from '@/features/market/NutrimaticCandleChart'
import { useNutrimaticMarketOptional } from '@/features/market/NutrimaticMarketProvider'
import {
  usePairAddressForCurrencies,
} from '@/features/market/usePairMarketData'
import type { Currency } from '@/sdk'

export function SwapMarketChartSection({
  compact = false,
  integrated = false,
  height = compact ? 160 : 360,
  fillHeight = false,
  /** @deprecated Interval is owned by the Nutrimatic hub; kept for call-site compat. */
  timeframe: _timeframe,
  onTimeframeChange: _onTimeframeChange,
  input,
  output,
}: {
  compact?: boolean
  /** Integrado en Nutri-Matic readout: sin marco glass ni título/pie duplicados. */
  integrated?: boolean
  height?: number
  fillHeight?: boolean
  timeframe?: CandleInterval
  onTimeframeChange?: (interval: CandleInterval) => void
  input?: Currency | null
  output?: Currency | null
}) {
  const t = useTranslation()
  const hub = useNutrimaticMarketOptional()
  const pairAddress = usePairAddressForCurrencies(input, output)

  useEffect(() => {
    if (hub && pairAddress) {
      hub.selectPairByPoolAddress(pairAddress)
    }
  }, [hub, pairAddress])

  const hubMatches =
    Boolean(hub?.selected) &&
    Boolean(pairAddress) &&
    hub?.poolRows.some(
      (r) =>
        r.poolAddress === pairAddress?.toLowerCase() &&
        r.listing.venueId === hub.selected?.venueId,
    )

  if (!pairAddress) {
    return (
      <MarketDataEmpty
        message={t.common.pairNotInMarketIndex}
        className={integrated ? 'h-full min-h-[120px]' : undefined}
      />
    )
  }

  if (!hub) {
    return (
      <MarketDataEmpty
        message={t.poolUi.noPairPriceData}
        className={integrated ? 'h-full min-h-[120px]' : undefined}
      />
    )
  }

  const series = hubMatches ? hub.detail.chart : null
  const loading = !hubMatches || hub.detail.loading || hub.detail.liveStatus === 'connecting'

  if (loading && !series) {
    return <MarketDataSkeleton className={integrated ? 'h-full min-h-[120px]' : 'h-80'} />
  }

  if (!series?.candles.length && !series?.partialCandle) {
    return (
      <MarketDataEmpty
        message={t.poolUi.noPairPriceData}
        className={integrated ? 'h-full min-h-[120px]' : undefined}
      />
    )
  }

  return (
    <NutrimaticCandleChart
      series={series}
      loading={loading}
      error={hub.detail.chartError}
      height={height}
      fillHeight={fillHeight}
      embedded={integrated}
      className={integrated ? 'h-full min-h-0 border-0 bg-transparent shadow-none' : undefined}
    />
  )
}
